import get from 'lodash/get';

import { isColumn } from '@lumapps/wrex-columns/utils/isColumn';
import { Path, Transforms, type Point, type ReactEditor } from '@lumapps/wrex/slate';
import { findParent } from '@lumapps/wrex/slate/utils/findParent';
import { focusAt } from '@lumapps/wrex/slate/utils/focusAt';
import { getSiblingPath } from '@lumapps/wrex/slate/utils/getSibling';
import { isSelectionEmpty } from '@lumapps/wrex/slate/utils/isSelectionEmpty';

import type { LinkPreviewAttributes, LinkPreviewEditor } from '../types';
import { createLinkPreview } from './createLinkPreview';

// Column structure depth: [columns, column, child]
const COLUMN_CHILD_DEPTH = 3;

/**
 * Insert link preview at or after the editor selection.
 * Returns the final insert position of the node.
 */
export function insertLinkPreview(
    editor: ReactEditor & LinkPreviewEditor,
    link: LinkPreviewAttributes,
    point?: Point | undefined,
) {
    const node = createLinkPreview({
        title: link.title,
        description: link.description,
        thumbnailUrl: link.thumbnailUrl,
        url: link.url || '',
        resource: link.resource,
    });

    const at = point || editor.selection?.focus;
    const canInsertOnSelection = at && isSelectionEmpty(editor, at.path);
    const columnParent = findParent(editor, at?.path, isColumn);

    let insertionPath;

    if (!columnParent) {
        // Insert at root level
        const after = getSiblingPath([get(at, ['path', 0]) as number], 'after');
        insertionPath = canInsertOnSelection ? [at.path[0]] : after;
    } else {
        // Insert inside column at child depth [columns, column, child]
        const currentPath = (at?.path || []).slice(0, COLUMN_CHILD_DEPTH);
        const after = Path.next(currentPath);
        insertionPath = canInsertOnSelection ? currentPath : after;
    }

    Transforms.insertNodes(editor, node, {
        at: insertionPath ?? [editor.children.length],
    });

    focusAt(editor, insertionPath);

    return insertionPath ?? [editor.children.length];
}
